/**
 * Toast.jsx  –  lightweight toast notification system
 *
 * Usage:
 *   import { useToast, ToastProvider } from '../components/common/Toast';
 *
 *   // Wrap your app (or just the part that needs toasts) with <ToastProvider>
 *   // Then in any component:
 *   const { toast } = useToast();
 *   toast.success('Saved!');
 *   toast.error('Something went wrong');
 *   toast.info('Review submitted');
 *   toast.warning('Please fill all fields');
 */

import { createContext, useContext, useState, useCallback, useRef,useEffect} from 'react';

/* ─── Context ────────────────────────────────────────────────────────────── */
const ToastContext = createContext(null);

/* ─── Hook ───────────────────────────────────────────────────────────────── */
// eslint-disable-next-line react-refresh/only-export-components -- hook lives alongside its provider; moving it would require updating every import site
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

/* ─── Provider ───────────────────────────────────────────────────────────── */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timerMap = useRef({});

   const dismiss = useCallback((id) => {
    if (timerMap.current[id]) {
      clearTimeout(timerMap.current[id]);
      delete timerMap.current[id];
    }
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
    );
    const exitTimer = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 320);
    timerMap.current[`exit_${id}`] = exitTimer;
  }, []);

  useEffect(() => {
    const timers = timerMap.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const show = useCallback(
    (message, { variant = 'info', duration = 3500 } = {}) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, message, variant, exiting: false }]);
      timerMap.current[id] = setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss]
  );

  const toast = {
    success: (msg, opts) => show(msg, { variant: 'success', ...opts }),
    error:   (msg, opts) => show(msg, { variant: 'error',   ...opts }),
    info:    (msg, opts) => show(msg, { variant: 'info',    ...opts }),
    warning: (msg, opts) => show(msg, { variant: 'warning', ...opts }),
    dismiss,
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/* ─── Variants ───────────────────────────────────────────────────────────── */
const VARIANTS = {
  success: {
    bg:     'rgba(76,175,137,0.96)',
    border: 'rgba(76,175,137,0.3)',
    icon:   (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="7" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5"/>
        <path d="M5 8l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  error: {
    bg:     'rgba(239,68,68,0.96)',
    border: 'rgba(239,68,68,0.3)',
    icon:   (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="7" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5"/>
        <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  warning: {
    bg:     'rgba(245,158,11,0.96)',
    border: 'rgba(245,158,11,0.3)',
    icon:   (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 2L14 13H2L8 2z" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M8 6v3M8 11v.5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  info: {
    bg:     'rgba(45,45,58,0.96)',
    border: 'rgba(179,157,219,0.3)',
    icon:   (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="7" stroke="rgba(179,157,219,0.6)" strokeWidth="1.5"/>
        <path d="M8 7v5M8 5v.5" stroke="#d1b7f3" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
};

/* ─── Single Toast ────────────────────────────────────────────────────────── */
function Toast({ id, message, variant = 'info', exiting, onDismiss }) {
  const v = VARIANTS[variant] || VARIANTS.info;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px',
        borderRadius: '14px',
        background: v.bg,
        border: `1px solid ${v.border}`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        color: '#fff',
        fontSize: '13px',
        fontWeight: '600',
        fontFamily: "'DM Sans', sans-serif",
        minWidth: '260px',
        maxWidth: '360px',
        cursor: 'pointer',
        userSelect: 'none',
        animation: exiting
          ? 'toastOut 0.3s ease forwards'
          : 'toastIn 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards',
        pointerEvents: 'auto',
      }}
      onClick={() => onDismiss(id)}
    >
      <span style={{ flexShrink: 0, lineHeight: 0 }}>{v.icon}</span>
      <span style={{ flex: 1, lineHeight: 1.4 }}>{message}</span>
      <button
        style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.6)',
          cursor: 'pointer',
          padding: '2px',
          lineHeight: 0,
          flexShrink: 0,
          fontSize: '16px',
        }}
        onClick={(e) => { e.stopPropagation(); onDismiss(id); }}
      >
        ×
      </button>
    </div>
  );
}

/* ─── Container ──────────────────────────────────────────────────────────── */
function ToastContainer({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <>
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(100%) scale(0.95); }
          to   { opacity: 1; transform: translateX(0)   scale(1); }
        }
        @keyframes toastOut {
          from { opacity: 1; transform: translateX(0)   scale(1); }
          to   { opacity: 0; transform: translateX(100%) scale(0.95); }
        }
      `}</style>
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => (
          <Toast key={t.id} {...t} onDismiss={onDismiss} />
        ))}
      </div>
    </>
  );
}